import { useState } from 'react';

import { motion } from 'motion/react';

import { AIAgentBanner } from '@src/components/AIAgentBanner';
import { ScreenshotGallery } from '@src/components/ScreenshotGallery';
import { Button } from '@src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

// Screenshot
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeftRight,
  ArrowRight,
  Award,
  BarChart3,
  BookOpen,
  Boxes,
  Bug,
  Building2,
  CheckCircle,
  CircleCheck,
  ClipboardList,
  Cloud,
  Crosshair,
  Database,
  FileCheck,
  FileText,
  Fingerprint,
  FolderOpen,
  Gauge,
  Globe,
  Heart,
  Layers,
  ListChecks,
  Lock,
  Network,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  Table,
  Tag,
  Target,
  Timer,
  TrendingUp,
  Users,
  Workflow,
  XCircle,
} from 'lucide-react';

import grcActions from '@src/assets/screenshots/grc-actions.webp';
import grcAuthorities from '@src/assets/screenshots/grc-authorities.webp';
import grcControls from '@src/assets/screenshots/grc-controls.webp';
import grcDueDiligenceEngagements from '@src/assets/screenshots/grc-due-diligence-engagements.webp';
import grcDueDiligencePackage from '@src/assets/screenshots/grc-due-diligence-package.webp';
import grcDueDiligence from '@src/assets/screenshots/grc-due-diligence.webp';
import grcEvidence from '@src/assets/screenshots/grc-evidence.webp';
import grcFrameworkProjects from '@src/assets/screenshots/grc-framework-projects.webp';
import grcIncidents from '@src/assets/screenshots/grc-incidents.webp';
import grcMonteCarlo from '@src/assets/screenshots/grc-monte-carlo.webp';
import grcRiskAnalytics from '@src/assets/screenshots/grc-risk-analytics.webp';
import grcRiskRegisters from '@src/assets/screenshots/grc-risk-registers.webp';
import grcRiskScenarios from '@src/assets/screenshots/grc-risk-scenarios.webp';
import AnswerSection from '@src/components/seo/AnswerSection';

const BOSSS_BLUE = '#2973a3';
const BG_COLOR = '#F8FAFC';

// Pain points that customers face
const painPoints = [
  {
    icon: XCircle,
    problem: 'Spreadsheet Chaos',
    description: 'Managing compliance with disconnected spreadsheets leads to version control nightmares, missed deadlines, and audit failures.',
    solution: 'Centralized platform with real-time collaboration, automated workflows, and single source of truth.',
  },
  {
    icon: Timer,
    problem: 'Audit Fatigue',
    description: 'Scrambling to collect evidence before audits, duplicating efforts across multiple frameworks, and endless auditor requests.',
    solution: 'Continuous compliance monitoring with automated evidence collection and multi-framework control mapping.',
  },
  {
    icon: AlertCircle,
    problem: 'Qualitative Guessing',
    description: 'Risk assessments based on subjective "high/medium/low" ratings with no quantitative financial impact analysis or data-driven prioritization.',
    solution: 'Monte Carlo simulations and FAIR methodology deliver quantitative risk analysis with financial impact modeling in dollars, not adjectives.',
  },
  {
    icon: Layers,
    problem: 'Framework Overload',
    description: 'Struggling to maintain compliance with NIST, ISO, CMMC, SOC 2, and other frameworks simultaneously without duplication.',
    solution: 'Intelligent control mapping that satisfies multiple frameworks with single implementation efforts.',
  },
];

// Governance capabilities from screenshots
const governanceCapabilities = [
  { name: 'Asset Classifications', description: 'Categorize assets by criticality and data sensitivity for informed decision-making', icon: Database },
  { name: 'Authorities', description: 'Define regulatory and industry authorities that govern your compliance requirements', icon: Building2 },
  { name: 'Baselines', description: 'Establish security baselines for systems using CIS, NIST, and custom standards', icon: ShieldCheck },
  { name: 'CSF Maturity Matrix', description: 'Track maturity across NIST Cybersecurity Framework functions', icon: Gauge },
  { name: 'Controls', description: 'Centralized control library with ownership, testing schedules, and evidence requirements', icon: Shield },
  { name: 'Cybersecurity Frameworks', description: 'Full support for NIST CSF with Govern, Identify, Protect, Detect, Respond, Recover', icon: Network },
  { name: 'FAIR Maturity Matrix', description: 'Quantitative risk analysis using Factor Analysis of Information Risk methodology', icon: BarChart3 },
  { name: 'Metrics', description: 'Define and track security KPIs including control coverage, risk posture, and compliance status', icon: TrendingUp },
  { name: 'Policies', description: 'Create, approve, and distribute organizational security policies with version control', icon: FileText },
  { name: 'Procedures', description: 'Document step-by-step procedures aligned with policies and controls', icon: ListChecks },
  { name: 'Programs', description: 'Manage security programs including people, processes, and technologies', icon: Workflow },
  { name: 'RACIs', description: 'Define accountability matrices for clear ownership and decision-making', icon: Users },
  { name: 'Rating Matrix', description: 'Standardize risk ratings based on likelihood and impact', icon: Target },
  { name: 'Records', description: 'Comprehensive records management with retention policies and legal holds', icon: FolderOpen },
  { name: 'Risk Library', description: 'Pre-built risk scenarios aligned with industry standards and threat intelligence', icon: BookOpen },
  { name: 'Risk Types', description: 'Categorize risks by operational, financial, compliance, and reputational dimensions', icon: AlertTriangle },
  { name: 'Standards', description: 'Map to ISO 27001, SOC 2, and industry-specific security standards', icon: Award },
  { name: 'Threat Matrix', description: 'Model threats based on MITRE ATT&CK and organizational context', icon: Crosshair },
];

// Risk capabilities from screenshots
const riskCapabilities = [
  { name: 'Asset Assessments', description: 'Evaluate effectiveness of processes, systems, and controls with actionable insights', icon: ClipboardList },
  { name: 'Authority Assessments', description: 'Assess compliance against NIST, ISO, FedRAMP, and government requirements', icon: Building2 },
  { name: 'Certification Projects', description: 'Streamline CMMC, FedRAMP, NIST, and ISO certification with unified workflows', icon: Award },
  { name: 'Custom Assessments', description: 'Build tailored assessments for cybersecurity, operational, or compliance domains', icon: Settings },
  { name: 'Incidents', description: 'Structured incident management with preparation, detection, containment, and recovery', icon: AlertCircle },
  { name: 'Key Indicators (KRIs)', description: 'Early warning signals that track risk thresholds and trigger proactive measures', icon: Gauge },
  { name: 'Risk Register', description: 'Living document tracking identified risks, ownership, and mitigation strategies', icon: FileCheck },
  { name: 'Risk Scenarios', description: 'Model potential threats with attack methods, targets, and Monte Carlo simulations', icon: Target },
  { name: 'Scans', description: 'Consolidate vulnerability and configuration scans with contextual risk scoring', icon: Search },
  { name: 'Threat Actors', description: 'Categorize adversaries from nation-states to insiders for threat modeling', icon: Fingerprint },
  { name: 'Vulnerabilities', description: 'Track and prioritize vulnerabilities aligned with NIST Cybersecurity Framework', icon: Bug },
];

// Compliance capabilities from screenshots
const complianceCapabilities = [
  {
    name: 'Risk Treatment Actions',
    description: 'Implement risk avoidance, reduction, sharing, or acceptance strategies aligned with your risk appetite',
    icon: Shield,
  },
  {
    name: 'Control Testing',
    description: 'Systematically evaluate control effectiveness and identify gaps aligned with industry standards',
    icon: CheckCircle,
  },
  {
    name: 'Evidence Management',
    description: 'Capture, classify, and archive evidence for ISO, NIST, FFIEC compliance and incident response',
    icon: FileText,
  },
  {
    name: 'Exception Handling',
    description: 'Document and track control exceptions with approval workflows and remediation timelines',
    icon: AlertTriangle,
  },
];

// Data Governance capabilities from screenshots
const dataGovernanceCapabilities = [
  {
    name: 'Data Catalog',
    description: 'Comprehensive data element catalog with standardized codes, descriptions, classifications, and ownership assignments',
    icon: Table,
  },
  {
    name: 'Data Classifications',
    description: 'Define sensitivity levels from Public to Restricted with authority hierarchies and entity ownership',
    icon: Tag,
  },
  {
    name: 'Data Collections',
    description: 'Group related data elements into logical collections for easier management and governance',
    icon: Boxes,
  },
  {
    name: 'Data Domains',
    description: 'Organize data by business domain for ownership assignment and policy application',
    icon: Globe,
  },
  {
    name: 'Data Sets',
    description: 'Define and manage data sets with metadata, lineage, and quality metrics',
    icon: Database,
  },
  {
    name: 'Data Transmissions',
    description: 'Track all data flows with encryption, protocols, source/destination systems, and network paths',
    icon: ArrowLeftRight,
  },
];

// Supported frameworks
const frameworks = [
  { name: 'NIST CSF', color: '#1e40af' },
  { name: 'ISO 27001', color: '#059669' },
  { name: 'SOC 2', color: '#7c3aed' },
  { name: 'CMMC', color: '#dc2626' },
  { name: 'FedRAMP', color: '#0891b2' },
  { name: 'HIPAA', color: '#db2777' },
  { name: 'PCI DSS', color: '#ea580c' },
  { name: 'GDPR', color: '#4f46e5' },
];

export function GRCPage() {
  const [activeTab, setActiveTab] = useState('governance');
  const onNavigateToTop = useNavigateToTop();

  const tabs = [
    { id: 'governance', label: 'Governance', icon: Shield, count: governanceCapabilities.length },
    { id: 'risk', label: 'Risk', icon: AlertTriangle, count: riskCapabilities.length },
    { id: 'compliance', label: 'Compliance', icon: CheckCircle, count: complianceCapabilities.length },
    { id: 'data', label: 'Data Governance', icon: Database, count: dataGovernanceCapabilities.length },
  ];

  const getActiveCapabilities = () => {
    switch (activeTab) {
      case 'compliance': return complianceCapabilities;
      case 'data': return dataGovernanceCapabilities;
      case 'governance': return governanceCapabilities;
      case 'risk': return riskCapabilities;
      default: return governanceCapabilities;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #dbeafe 50%, #bfdbfe 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-20 left-20"
            animate={{ rotate: [0, 10, -10, 0], y: [0, -15, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Shield className="w-8 h-8" style={{ color: `${BOSSS_BLUE}50` }} />
          </motion.div>

          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            className="absolute bottom-40 right-20"
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <CheckCircle className="w-6 h-6" style={{ color: `${BOSSS_BLUE}40` }} />
          </motion.div>

          <motion.div
            animate={{ y: [0, -20, 0] }}
            className="absolute top-40 right-1/4"
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <FileText className="w-5 h-5" style={{ color: `${BOSSS_BLUE}45` }} />
          </motion.div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div
                style={{ backgroundColor: `${BOSSS_BLUE}15`, color: BOSSS_BLUE }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <Shield className="w-4 h-4 mr-2" />
                Governance, Risk &amp; Compliance
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                End the Compliance Chaos. Take Control of Risk.
              </h1>

              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                BOSSS GRC replaces scattered spreadsheets and siloed tools with a unified platform
                delivering
                {' '}
                <strong>continuous compliance monitoring</strong>
                —not point-in-time assessments.
                Get real-time visibility into your security posture, automated evidence collection,
                and intelligent control mapping across multiple frameworks simultaneously.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-white/80 rounded-lg shadow-sm">
                  <div style={{ color: BOSSS_BLUE }} className="text-3xl font-bold">50+</div>
                  <div className="text-sm text-gray-600">GRC Capabilities</div>
                </div>

                <div className="text-center p-4 bg-white/80 rounded-lg shadow-sm">
                  <div style={{ color: BOSSS_BLUE }} className="text-3xl font-bold">60+</div>
                  <div className="text-sm text-gray-600">Authorities</div>
                </div>

                <div className="text-center p-4 bg-white/80 rounded-lg shadow-sm">
                  <div style={{ color: BOSSS_BLUE }} className="text-3xl font-bold">1</div>
                  <div className="text-sm text-gray-600">Unified Platform</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    style={{ backgroundColor: BOSSS_BLUE }}
                    className="text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl"
                    onClick={() => onNavigateToTop('/demo')}
                  >
                    Schedule a Demo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>

                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    variant="outline"
                    style={{ borderColor: BOSSS_BLUE, color: BOSSS_BLUE }}
                    className="px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg"
                    onClick={() => onNavigateToTop('/contact')}
                  >
                    Contact Us
                  </Button>
                </motion.div>
              </div>
            </motion.div>

            {/* Three Pillars Visualization */}
            <motion.div
              className="relative"
              animate={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="grid grid-cols-3 gap-4">
                {[
                  { title: 'Governance', icon: Shield, items: ['Policies', 'Controls', 'Standards', 'Frameworks'], color: '#1e40af' },
                  { title: 'Risk', icon: AlertTriangle, items: ['Assessments', 'Register', 'Scenarios', 'Incidents'], color: '#dc2626' },
                  { title: 'Compliance', icon: CheckCircle, items: ['Testing', 'Evidence', 'Actions', 'Exceptions'], color: '#059669' },
                ].map((pillar, index) => (
                  <motion.div
                    key={pillar.title}
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 30 }}
                    style={{ borderTopColor: pillar.color }}
                    className="bg-white rounded-xl shadow-lg p-5 border-t-4"
                    transition={{ duration: 0.6, delay: 0.4 + index * 0.15 }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <pillar.icon className="w-5 h-5" style={{ color: pillar.color }} />
                      <h3 className="font-bold text-gray-900">{pillar.title}</h3>
                    </div>

                    <ul className="space-y-2">
                      {pillar.items.map((item, i) => (
                        <li key={i} className="text-sm text-gray-600 flex items-center">
                          <CircleCheck className="w-3 h-3 mr-2" style={{ color: pillar.color }} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/platform/grc" />


      {/* Pain Points Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              We Understand Your GRC Challenges
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Security and compliance teams face mounting pressure. Here&apos;s how BOSSS transforms your biggest pain points into strengths.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-red-100">
                        <point.icon className="w-6 h-6 text-red-600" />
                      </div>

                      <div>
                        <CardTitle className="text-xl text-gray-900 mb-2">
                          {point.problem}
                        </CardTitle>

                        <p className="text-gray-600 text-sm">
                          {point.description}
                        </p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div style={{ backgroundColor: `${BOSSS_BLUE}08` }} className="flex items-start gap-4 p-4 rounded-lg">
                      <div style={{ backgroundColor: `${BOSSS_BLUE}20` }} className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4" style={{ color: BOSSS_BLUE }} />
                      </div>

                      <div>
                        <p style={{ color: BOSSS_BLUE }} className="text-sm font-medium">BOSSS Solution</p>
                        <p className="text-sm text-gray-700 mt-1">{point.solution}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Screenshot Section */}
      <section className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A unified dashboard for managing frameworks, controls, risks, and compliance—all in one place.
            </p>
          </motion.div>

          {/* Screenshot Tabs */}
          <ScreenshotGallery
            urlPath="grc"
            accentColor={BOSSS_BLUE}
            screenshots={[
              { src: grcMonteCarlo, label: 'Monte Carlo', alt: 'BOSSS GRC multi-scenario Monte Carlo simulation with exposure scatter and per-scenario VaR and CVaR rollup' },
              { src: grcRiskAnalytics, label: 'Risk Analytics', alt: 'BOSSS GRC risk analytics with control strength, expected annual loss and simulated loss distribution' },
              { src: grcRiskScenarios, label: 'Risk Scenarios', alt: 'BOSSS GRC FAIR-quantified risk scenarios with likelihood, loss magnitude and annualized loss' },
              { src: grcRiskRegisters, label: 'Risk Registers', alt: 'BOSSS GRC risk registers with ownership, treatment status and confidence tiers' },
              { src: grcAuthorities, label: 'Authorities', alt: 'BOSSS GRC authorities covering CIS v8, FBI CJIS, NIST CSF 2.0 and PCI DSS 4.0 with verified requirement coverage' },
              { src: grcControls, label: 'Controls', alt: 'BOSSS GRC common controls with NIST CSF function codes, named owners, requirement counts and implementation status' },
              { src: grcFrameworkProjects, label: 'Certifications', alt: 'BOSSS GRC certification projects for CMMC 2.0, HIPAA Security Rule, IEC 62443 OT, FBI CJIS, PCI DSS v4.0 and SOC 2 Type II' },
              { src: grcIncidents, label: 'Incidents', alt: 'BOSSS GRC incident register with severity, category, reporting timeline, location and named reporter' },
              { src: grcActions, label: 'Actions', alt: 'BOSSS GRC remediation actions with owner, funding source, budget and spend, urgency, linked asset and planned residual risk' },
              { src: grcDueDiligence, label: 'Due Diligence', alt: 'BOSSS GRC third-party due diligence dashboard with package counts, phase distribution and task progress' },
              {
                src: grcDueDiligencePackage,
                label: 'Package Timeline',
                alt: 'BOSSS GRC due diligence package with a five-phase timeline from scope approval through discovery, assessment and reporting to closeout, each phase owned and dated',
              },
              {
                src: grcDueDiligenceEngagements,
                label: 'Linked Engagements',
                alt: 'BOSSS GRC due diligence package linked to a completed external network and web assessment, showing the engagement lead and its finding count including criticals',
              },
              { src: grcEvidence, label: 'Evidence', alt: 'BOSSS GRC evidence collection linked to controls and audit preparation' },
            ]}
          />

          {/* Feature Callouts */}
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
          >
            {[
              { icon: Shield, label: 'Framework Compliance', value: 'Multi-standard' },
              { icon: Target, label: 'Control Mapping', value: 'Automated' },
              { icon: AlertTriangle, label: 'Risk Management', value: 'Real-time' },
              { icon: FileText, label: 'Audit Reports', value: 'One-click' },
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-white rounded-xl shadow-md border border-gray-100">
                <item.icon style={{ color: BOSSS_BLUE }} className="w-6 h-6 mx-auto mb-2" />
                <p className="text-lg font-bold text-gray-900">{item.value}</p>
                <p className="text-xs text-gray-500">{item.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Quantitative Risk Analysis Highlight */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6 bg-blue-500/30 text-blue-100 border border-blue-400/30">
                <BarChart3 className="w-4 h-4 mr-2" />
                Industry-Leading Capability
              </div>

              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Quantitative Risk Analysis with Monte Carlo Simulations
              </h2>

              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Stop guessing with &quot;high/medium/low&quot; risk ratings. BOSSS delivers
                {' '}
                <strong>data-driven, quantitative risk analysis</strong>
                {' '}
                using FAIR methodology and Monte Carlo simulations—giving you financial impact in
                {' '}
                <strong>dollars, not adjectives</strong>
                .
              </p>

              <div className="space-y-4 mb-8">
                {[
                  {
                    icon: BarChart3,
                    title: 'FAIR Methodology',
                    description: 'Industry-standard Factor Analysis of Information Risk (FAIR) framework for quantitative analysis',
                  },
                  {
                    icon: Target,
                    title: 'Monte Carlo Simulations',
                    description: 'Run 10,000+ iterations to model risk probability distributions and financial exposure',
                  },
                  {
                    icon: TrendingUp,
                    title: 'Financial Impact Modeling',
                    description: 'Calculate loss expectancy in real dollars: $50K-$2.5M potential exposure, not "High Risk"',
                  },
                  {
                    icon: Gauge,
                    title: 'Risk Appetite Alignment',
                    description: 'Compare quantified risks against board-approved risk appetite thresholds for data-driven decisions',
                  },
                ].map((feature, i) => (
                  <motion.div
                    key={i}
                    viewport={{ once: true }}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                    className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500/30 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-blue-100" />
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">{feature.title}</h3>
                      <p className="text-sm text-blue-100">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="inline-flex items-center gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <Award className="w-8 h-8 text-yellow-400 flex-shrink-0" />

                <div>
                  <p className="text-white font-semibold">Competitive Differentiator</p>
                  <p className="text-sm text-blue-100">Most GRC platforms only offer qualitative risk scoring—BOSSS delivers true quantitative analysis</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="relative"
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">Ransomware Attack Scenario</h4>

                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Critical Risk
                    </span>
                  </div>

                  <p className="text-sm text-gray-600">Financial impact analysis after 10,000 Monte Carlo iterations</p>
                </div>

                <div className="space-y-6">
                  {/* Distribution Chart Representation */}
                  <div className="relative h-40 bg-gradient-to-r from-green-50 via-yellow-50 to-red-50 rounded-lg border border-gray-200 p-4">
                    <div className="absolute inset-0 flex items-end justify-around p-4">
                      {[20, 35, 55, 75, 90, 100, 95, 70, 45, 25].map((height, i) => (
                        <div
                          key={i}
                          style={{ height: `${height}%` }}
                          className="w-8 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t"
                        />
                      ))}
                    </div>
                  </div>

                  {/* Statistical Results */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Minimum Loss</p>
                      <p className="text-2xl font-bold text-gray-900">$50K</p>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Maximum Loss</p>
                      <p className="text-2xl font-bold text-gray-900">$2.5M</p>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600 mb-1">Mean Loss (Expected)</p>
                      <p className="text-2xl font-bold text-blue-900">$485K</p>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600 mb-1">90th Percentile</p>
                      <p className="text-2xl font-bold text-blue-900">$950K</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-yellow-50 rounded-lg border border-yellow-200">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Risk Appetite Threshold</p>
                      <p className="text-xs text-gray-600">Board-approved annual limit</p>
                    </div>

                    <p className="text-2xl font-bold text-gray-900">$500K</p>
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />

                    <p className="text-sm font-medium text-red-900">
                      Expected loss approaches risk appetite—mitigation required
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comprehensive Capabilities Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Complete GRC Capabilities
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need for enterprise governance, risk management, and compliance—built into one integrated platform.
            </p>
          </motion.div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-12 overflow-x-auto px-4 -mx-4 sm:mx-0 sm:px-0">
            <div className="inline-flex bg-white rounded-xl p-1 shadow-md border border-gray-200 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  style={{
                    backgroundColor: activeTab === tab.id ? BOSSS_BLUE : 'transparent',
                  }}
                  className={`flex items-center gap-2 px-3 sm:px-6 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}

                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Capabilities Grid */}
          <motion.div
            key={activeTab}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            initial={{ opacity: 0, y: 20 }}
            className={`grid gap-4 ${
              activeTab === 'compliance' || activeTab === 'data'
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}
          >
            {getActiveCapabilities().map((capability, index) => (
              <motion.div
                key={capability.name}
                animate={{ opacity: 1, scale: 1 }}
                initial={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-lg hover:border-blue-200 transition-all duration-200 group">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div
                        style={{ backgroundColor: `${BOSSS_BLUE}12` }}
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                      >
                        <capability.icon className="w-5 h-5" style={{ color: BOSSS_BLUE }} />
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1 group-hover:text-blue-700 transition-colors">
                          {capability.name}
                        </h4>

                        <p className="text-sm text-gray-600 leading-relaxed">
                          {capability.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pentest → GRC Risk Loop Callout */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-8 border-2"
            style={{ borderColor: `${BOSSS_BLUE}30`, backgroundColor: `${BOSSS_BLUE}06` }}
          >
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
              <div className="flex-shrink-0">
                <div
                  style={{ backgroundColor: `${BOSSS_BLUE}15` }}
                  className="w-16 h-16 rounded-2xl flex items-center justify-center"
                >
                  <Workflow className="w-8 h-8" style={{ color: BOSSS_BLUE }} />
                </div>
              </div>

              <div className="flex-1">
                <div
                  style={{ backgroundColor: `${BOSSS_BLUE}15`, color: BOSSS_BLUE }}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold mb-3"
                >
                  Unique Platform Integration
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Pentest Findings Automatically Become GRC Risk Items
                </h3>

                <p className="text-gray-600 leading-relaxed">
                  When the BOSSS Penetration Testing module discovers a vulnerability, it automatically
                  creates a risk item in the GRC
                  {' '}
                  <strong>Risk Register</strong>
                  —linked to the affected asset,
                  mapped to NIST controls, and assigned an owner for remediation tracking. No copy-paste,
                  no spreadsheet exports, no information loss between teams.

                  <span className="italic text-gray-500 ml-2">
                    No standalone pentest platform does this. No standalone GRC platform does this.
                    Only a true Cybersecurity ERP can close this loop.
                  </span>
                </p>
              </div>

              <div className="flex-shrink-0 flex flex-col gap-2 min-w-[140px]">
                {[
                  { step: '1', label: 'Pentest finds CVE' },
                  { step: '2', label: 'Risk auto-created' },
                  { step: '3', label: 'Owner assigned' },
                  { step: '4', label: 'Remediation tracked' },
                ].map((s) => (
                  <div key={s.step} className="flex items-center gap-2">
                    <div
                      style={{ backgroundColor: BOSSS_BLUE }}
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    >
                      {s.step}
                    </div>

                    <span className="text-sm text-gray-700">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Framework Support Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              One Control. Multiple Frameworks.
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Map your security controls once and satisfy requirements across all major compliance frameworks.
              Stop duplicating work—start demonstrating continuous compliance.
            </p>
          </motion.div>

          {/* Framework Badges */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {frameworks.map((framework, index) => (
              <motion.div
                key={framework.name}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -2 }}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="px-5 py-3 rounded-lg font-medium shadow-md cursor-default"
                style={{
                  backgroundColor: `${framework.color}12`,
                  color: framework.color,
                  border: `1px solid ${framework.color}30`,
                }}
              >
                {framework.name}
              </motion.div>
            ))}
          </div>

          {/* Control Mapping Visualization */}
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8 md:p-12"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              <div className="text-center lg:text-left">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Intelligent Control Mapping</h3>

                <p className="text-gray-600 mb-6">
                  When you implement a control in BOSSS, it automatically maps to all applicable
                  framework requirements—eliminating redundant work and ensuring comprehensive coverage.
                </p>

                <ul className="space-y-3 text-left">
                  {[
                    'Automatic crosswalk between frameworks',
                    'Gap analysis identifies missing controls',
                    'Real-time compliance scoring',
                    'Audit-ready evidence packages',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center text-gray-700">
                      <CheckCircle style={{ color: BOSSS_BLUE }} className="w-5 h-5 mr-3 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="font-semibold text-gray-900">Control: AC-2 Account Management</h4>

                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Implemented
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { framework: 'NIST 800-53', req: 'AC-2', status: 'satisfied' },
                      { framework: 'ISO 27001', req: 'A.9.2.1', status: 'satisfied' },
                      { framework: 'SOC 2', req: 'CC6.1', status: 'satisfied' },
                      { framework: 'CMMC', req: 'AC.L2-3.1.1', status: 'satisfied' },
                      { framework: 'FedRAMP', req: 'AC-2', status: 'satisfied' },
                      { framework: 'HIPAA', req: '164.312(a)', status: 'satisfied' },
                      { framework: 'PCI DSS', req: '8.1.1', status: 'satisfied' },
                      { framework: 'GDPR', req: 'Art. 32', status: 'partial' },
                    ].map((mapping, i) => (
                      <motion.div
                        key={i}
                        viewport={{ once: true }}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: i * 0.05 }}
                        className={`p-3 rounded-lg border ${
                          mapping.status === 'satisfied'
                            ? 'bg-green-50 border-green-200'
                            : 'bg-yellow-50 border-yellow-200'
                        }`}
                      >
                        <p className="text-xs font-medium text-gray-900">{mapping.framework}</p>
                        <p className="text-sm font-mono text-gray-600">{mapping.req}</p>

                        <div className="flex items-center mt-1">
                          {mapping.status === 'satisfied'
                            ? (
                              <CheckCircle className="w-3 h-3 text-green-600" />
                            )
                            : (
                              <AlertCircle className="w-3 h-3 text-yellow-600" />
                            )}

                          <span
                            className={`text-xs ml-1 ${
                              mapping.status === 'satisfied' ? 'text-green-600' : 'text-yellow-600'
                            }`}
                          >
                            {mapping.status === 'satisfied' ? 'Satisfied' : 'Partial'}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Data Governance Deep Dive Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div
              style={{ backgroundColor: '#8b5cf615', color: '#8b5cf6' }}
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <Database className="w-4 h-4 mr-2" />
              Data Governance
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Complete Visibility Into Your Data Landscape
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Know what data you have, where it flows, and how it&apos;s protected—essential for
              GDPR, CCPA, HIPAA, and privacy program compliance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Data Catalog Preview */}
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-white rounded-xl shadow-xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div style={{ backgroundColor: '#8b5cf615' }} className="w-10 h-10 rounded-lg flex items-center justify-center">
                      <Table className="w-5 h-5" style={{ color: '#8b5cf6' }} />
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900">Data Catalog</h3>
                      <p className="text-xs text-gray-500">All Data Elements</p>
                    </div>
                  </div>

                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                    3 Pages
                  </span>
                </div>

                {/* Sample Data Elements */}
                <div className="space-y-2 mb-4">
                  {[
                    { code: 'D.14.1', label: 'Access to Care', classification: 'Confidential' },
                    { code: 'DE.1.1', label: 'Account Number', classification: 'Confidential' },
                    { code: 'C.3.2.1', label: 'Accounting', classification: 'Internal' },
                    { code: 'DE.1.2', label: 'Address', classification: 'Protected' },
                    { code: 'D.20.4', label: 'Advising and Consulting', classification: 'Public' },
                  ].map((element, i) => (
                    <motion.div
                      key={i}
                      viewport={{ once: true }}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-xs font-mono text-gray-500 w-16">{element.code}</span>
                      <span className="text-sm text-gray-900 flex-1">{element.label}</span>

                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          element.classification === 'Confidential'
                            ? 'bg-red-100 text-red-700' :
                            element.classification === 'Protected'
                              ? 'bg-orange-100 text-orange-700' :
                              element.classification === 'Internal'
                                ? 'bg-blue-100 text-blue-700' :
                                'bg-green-100 text-green-700'
                        }`}
                      >
                        {element.classification}
                      </span>
                    </motion.div>
                  ))}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-100">
                  {[
                    { label: 'Domains', value: '12' },
                    { label: 'Collections', value: '8' },
                    { label: 'Owners', value: '15' },
                  ].map((stat, i) => (
                    <div key={i} className="text-center p-2 bg-gray-50 rounded-lg">
                      <p style={{ color: '#8b5cf6' }} className="text-lg font-bold">{stat.value}</p>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Data Transmissions & Classifications */}
            <motion.div
              className="space-y-6"
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              {/* Classifications Preview */}
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <Tag className="w-5 h-5" style={{ color: '#8b5cf6' }} />
                  <h4 className="font-semibold text-gray-900">Data Classifications</h4>
                  <span className="ml-auto text-xs text-gray-500">Custom Levels</span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {[
                    { name: 'Public', color: '#22c55e' },
                    { name: 'Internal', color: '#3b82f6' },
                    { name: 'Confidential', color: '#f59e0b' },
                    { name: 'Secret', color: '#ef4444' },
                    { name: 'Protected', color: '#8b5cf6' },
                    { name: 'Restricted', color: '#dc2626' },
                  ].map((cls, i) => (
                    <span
                      key={i}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium"
                      style={{ backgroundColor: `${cls.color}15`, color: cls.color }}
                    >
                      {cls.name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Transmissions Preview */}
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <ArrowLeftRight className="w-5 h-5" style={{ color: '#8b5cf6' }} />
                  <h4 className="font-semibold text-gray-900">Data Transmissions</h4>
                  <span className="ml-auto text-xs text-gray-500">All Data Flows</span>
                </div>

                {/* Sample Transmission */}
                <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-900">Adaptive logical paradigm Data Flow</span>
                    <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">Active</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {[
                      { label: 'Type', value: 'OpenVPN' },
                      { label: 'Location', value: 'Satellite Internet' },
                      { label: 'Encryption', value: 'CAST5 (256-bit)' },
                      { label: 'Protocol', value: 'API' },
                      { label: 'Direction', value: 'Inbound Only' },
                      { label: 'Authentication', value: 'WebAuthn' },
                    ].map((field, i) => (
                      <div key={i}>
                        <span className="text-gray-500">
                          {field.label}
                          :
                          {' '}
                        </span>

                        <span className="text-gray-900 font-medium">{field.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Certification Projects Feature */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Streamline Your Certification Journey
              </h2>

              <p className="text-lg text-gray-600 mb-6">
                Whether you&apos;re pursuing CMMC, FedRAMP, ISO 27001, or SOC 2 certification,
                BOSSS provides a structured workflow that integrates governance, risk, and
                control management into one unified project.
              </p>

              <div className="space-y-4">
                {[
                  { title: 'Centralized Documentation', desc: 'All evidence, assessments, and corrective actions in one place' },
                  { title: 'Progress Tracking', desc: 'Real-time dashboards showing certification readiness' },
                  { title: 'Auditor Collaboration', desc: 'Secure portal for auditors to review evidence and findings' },
                  { title: 'Gap Remediation', desc: 'Automated workflows to address identified gaps before audit' },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    viewport={{ once: true }}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    className="flex items-start gap-4"
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                  >
                    <div
                      style={{ backgroundColor: `${BOSSS_BLUE}15` }}
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    >
                      <CheckCircle className="w-4 h-4" style={{ color: BOSSS_BLUE }} />
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold text-gray-900">SOC 2 Type II Certification</h4>

                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    In Progress
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Overall Progress</span>
                    <span className="font-medium" style={{ color: BOSSS_BLUE }}>78%</span>
                  </div>

                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <motion.div
                      initial={{ width: '0%' }}
                      viewport={{ once: true }}
                      className="h-3 rounded-full"
                      whileInView={{ width: '78%' }}
                      style={{ backgroundColor: BOSSS_BLUE }}
                      transition={{ duration: 1, delay: 0.3 }}
                    />
                  </div>
                </div>

                {/* Trust Service Criteria */}
                <div className="space-y-3">
                  {[
                    { name: 'Security (CC)', progress: 92 },
                    { name: 'Availability (A)', progress: 85 },
                    { name: 'Confidentiality (C)', progress: 78 },
                    { name: 'Processing Integrity (PI)', progress: 65 },
                    { name: 'Privacy (P)', progress: 70 },
                  ].map((criteria, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <span className="text-sm text-gray-600 w-40">{criteria.name}</span>

                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <motion.div
                          initial={{ width: '0%' }}
                          viewport={{ once: true }}
                          className="h-2 rounded-full"
                          whileInView={{ width: `${criteria.progress}%` }}
                          transition={{ duration: 0.8, delay: 0.5 + i * 0.1 }}
                          style={{
                            backgroundColor: criteria.progress >= 80
                              ? '#10b981' :
                              criteria.progress >= 60 ? '#f59e0b' : '#ef4444',
                          }}
                        />
                      </div>

                      <span className="text-sm font-medium text-gray-900 w-12 text-right">
                        {criteria.progress}
                        %
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Built for Regulated Industries
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              BOSSS GRC adapts to your industry&apos;s specific compliance and governance requirements.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Building2,
                title: 'Financial Services',
                frameworks: ['SOX', 'PCI DSS', 'GLBA'],
                benefits: ['SOX Section 404 compliance', 'Operational risk management', 'Regulatory change tracking'],
              },
              {
                icon: Heart,
                title: 'Healthcare',
                frameworks: ['HIPAA', 'HITECH', 'FDA 21 CFR'],
                benefits: ['HIPAA compliance tracking', 'Privacy risk assessments', 'Breach response workflows'],
              },
              {
                icon: Lock,
                title: 'Government',
                frameworks: ['FedRAMP', 'FISMA', 'CMMC'],
                benefits: ['FedRAMP authorization', 'Continuous monitoring', 'Security control management'],
              },
              {
                icon: Cloud,
                title: 'Technology',
                frameworks: ['SOC 2', 'ISO 27001', 'GDPR'],
                benefits: ['SaaS security posture', 'Customer trust reports', 'Data protection compliance'],
              },
            ].map((industry, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 group">
                  <CardHeader className="pb-4">
                    <div
                      style={{ backgroundColor: `${BOSSS_BLUE}15` }}
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    >
                      <industry.icon className="w-6 h-6" style={{ color: BOSSS_BLUE }} />
                    </div>

                    <CardTitle className="text-lg group-hover:text-blue-600 transition-colors">
                      {industry.title}
                    </CardTitle>

                    <div className="flex flex-wrap gap-1 mt-2">
                      {industry.frameworks.map((fw, i) => (
                        <span key={i} className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                          {fw}
                        </span>
                      ))}
                    </div>
                  </CardHeader>

                  <CardContent>
                    <ul className="space-y-2">
                      {industry.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start text-sm text-gray-600">
                          <CheckCircle style={{ color: BOSSS_BLUE }} className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Agent Banner */}
      <AIAgentBanner
        toolsCount={35}
        agentName="GRC Agent"
        accentColor={BOSSS_BLUE}
        description="Your AI-powered compliance copilot. Ask about risks, controls, frameworks, or compliance status—get instant, accurate answers."
        exampleQueries={[
          '"What critical risks need attention this week?"',
          '"Show me our SOC 2 compliance status"',
          '"Which controls failed testing last month?"',
          '"Create a new risk for ransomware scenario"',
        ]}
      />

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: BOSSS_BLUE }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Transform Your GRC Operations?
            </h2>

            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              See how BOSSS GRC can eliminate compliance chaos, reduce audit fatigue, and give you
              real-time visibility into your security posture.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  className="bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                  onClick={() => onNavigateToTop('/demo')}
                >
                  Schedule Your Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>

              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  variant="outline"
                  className="border-2 border-white/80 text-white bg-white/10 hover:bg-white/20 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold backdrop-blur-sm"
                  onClick={() => onNavigateToTop('/contact')}
                >
                  Contact Sales
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
